/**
 * The public tracking API — the only file app code needs to read.
 *
 * Every entry point is a no-op when there is no valid measurement id, when there
 * is no browser (unit tests / SSR), or when the tag failed to load. Analytics
 * must never throw into app code, and a lab with no route to Google must behave
 * exactly like one with analytics switched off.
 *
 * Two settings must match on the GA4 property side, because no client code can
 * override them:
 *  - Enhanced measurement's "page changes based on browser history events" must
 *    be OFF. It reports `location.href` verbatim, which bypasses the sanitizing
 *    in `sanitize-path.ts` and would record search filters holding patient names.
 *  - Enhanced measurement's "site search" must be OFF, for the same reason.
 */
import { environment } from 'src/environments/environment';

import { DEFAULT_LOAD_TIMEOUT_MS } from './analytics.constants';
import type {
  AnalyticsConfig,
  AnalyticsEventName,
  AnalyticsEventParams,
  AnalyticsFeature,
  AnalyticsFunnel,
  AnalyticsFunnelSteps,
  AnalyticsIdentity,
  LabKey,
} from './analytics.types';
import { createGtagClient, type GtagClient, type GtagWindow } from './gtag-client';
import { resolveAnalyticsLab } from './lab';
import { resolveMeasurementId } from './measurement-id';
import { normalizeKey, pseudonymize, redactText, scrubParams } from './redact';
import { sanitizeAnalyticsPath } from './sanitize-path';

/** Event names used across the app, for call sites that prefer a constant. */
export const ANALYTICS_EVENTS = {
  pageView: 'page_view',
  click: 'ui_click',
  featureUse: 'feature_use',
  funnelStep: 'funnel_step',
  appError: 'app_error',
} as const satisfies Record<string, AnalyticsEventName>;

let initialized = false;
let client: GtagClient | null = null;
let lab: LabKey = '';
let allowedQueryParams: readonly string[] = [];
/** Previous sanitized location, sent as `page_referrer` so navigation flows link up. */
let lastPageLocation: string | null = null;

/** The browser window, or null under unit tests / any non-browser host. */
function getWindow(): GtagWindow | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  return window as GtagWindow;
}

/**
 * Prepare the queue and configure every property this lab reports to.
 * Idempotent, and called automatically by the first `track*` call — pass a
 * config explicitly only to override the defaults taken from `environment`.
 */
export function initAnalytics(config: AnalyticsConfig = {}): void {
  if (initialized) return;

  const win = getWindow();
  if (!win) return;

  initialized = true;

  const hostname = win.location?.hostname ?? '';
  lab = resolveAnalyticsLab(hostname);

  if (config.enabled === false) return;
  if (config.respectDoNotTrack === true && win.navigator?.doNotTrack === '1') {
    return;
  }

  const measurementId = resolveMeasurementId(
    config.measurementId ?? environment.gaMeasurementId,
  );
  if (!measurementId) return;

  allowedQueryParams = config.allowedQueryParams ?? [];
  client = createGtagClient(win, {
    measurementId,
    loadTimeoutMs: config.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS,
  });

  client.command('js', new Date());
  client.command('config', measurementId, {
    // Page views are sent by hand from sanitized paths; the automatic one would
    // report the raw URL.
    send_page_view: false,
    app_version: environment.appVersion,
    debug_mode: !environment.production,
    cookie_domain: config.cookieDomain === 'auto' ? 'auto' : hostname,
  });
  // The dimension that splits the single property by lab.
  client.command('set', 'user_properties', { lab });
}

/**
 * The client to send through, or null when tracking is off. Also triggers the
 * lazy tag load, so the fetch happens on first use rather than at bootstrap.
 */
function activeClient(): GtagClient | null {
  if (!initialized) initAnalytics();
  if (!client || client.hasFailed) return null;
  client.ensureLoaded();
  return client;
}

/**
 * Send an event. The name and the parameter shape both come from
 * `AnalyticsEventParams`, so a new event is added there first.
 */
export function trackEvent<N extends AnalyticsEventName>(
  name: N,
  params: AnalyticsEventParams[N],
): void {
  const active = activeClient();
  if (!active) return;

  const eventName = normalizeKey(name);
  if (!eventName) return;

  active.command(
    'event',
    eventName as AnalyticsEventName,
    scrubParams(params as Readonly<Record<string, unknown>>),
  );
}

/** Send a page view for an Angular navigation. Pass `router.url`. */
export function trackPageView(url: string, title?: string): void {
  const win = getWindow();
  if (!win || !activeClient()) return;

  const path = sanitizeAnalyticsPath(url, allowedQueryParams);
  const location = `${win.location.origin}${path}`;

  trackEvent('page_view', {
    page_location: location,
    page_title: redactText(title ?? win.document.title ?? ''),
    ...(lastPageLocation ? { page_referrer: lastPageLocation } : {}),
  });

  lastPageLocation = location;
}

/**
 * Attach the signed-in session's non-identifying attributes, which is what makes
 * the per-lab and per-role breakdowns possible. Call on login, on `/me`
 * resolution, and with `null` on logout.
 */
export function setAnalyticsUser(identity: AnalyticsIdentity | null): void {
  const active = activeClient();
  if (!active) return;

  if (!identity) {
    active.command('set', { user_id: null });
    active.command('set', 'user_properties', {
      lab,
      user_role: null,
      is_main_lab: null,
      is_sso: null,
    });
    return;
  }

  active.command('set', {
    user_id: identity.userId ? pseudonymize(String(identity.userId)) : null,
  });
  active.command('set', 'user_properties', {
    lab: identity.lab || lab,
    user_role: redactText(identity.role ?? '') ?? null,
    is_main_lab: !!identity.isMainLab,
    is_sso: !!identity.isSSO,
  });
}

/** A button, link, tab or menu activation. */
export function trackClick(label: string, area?: AnalyticsFeature): void {
  trackEvent('ui_click', { ui_label: label, ui_area: area });
}

/** Use of an application feature, for "which features earn their keep" reporting. */
export function trackFeatureUse(feature: AnalyticsFeature, action?: string): void {
  trackEvent('feature_use', { feature, feature_action: action });
}

/**
 * One step of a named journey, e.g. `trackFunnelStep('report', 'exported', 4)`.
 * The step is typed against its funnel, so a misspelled step is a build error
 * rather than a funnel that never converts.
 */
export function trackFunnelStep<F extends AnalyticsFunnel>(
  funnel: F,
  step: AnalyticsFunnelSteps[F],
  stepIndex?: number,
): void {
  trackEvent('funnel_step', { funnel, step, step_index: stepIndex });
}

/**
 * An application error worth counting. The description is redacted like any
 * other value, since error text often quotes the data that caused it.
 */
export function trackAppError(
  description: string,
  context: Omit<AnalyticsEventParams['app_error'], 'error_description'> = {},
): void {
  trackEvent('app_error', {
    ...context,
    error_description: redactText(description) ?? 'unknown',
  });
}

/** The lab this session reports as. Useful in a debug view or a support dump. */
export function currentAnalyticsLab(): LabKey {
  if (!initialized) initAnalytics();
  return lab;
}

/**
 * Drop all module state. Intended for tests — production code has no reason to
 * re-init, and this does not remove an already-injected tag.
 */
export function resetAnalytics(): void {
  initialized = false;
  client = null;
  lab = '';
  allowedQueryParams = [];
  lastPageLocation = null;
}
